'use client';

import * as React from 'react';
import * as D from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from './cn';
import { IconButton } from './IconButton';
import { useUILabels } from './UIProvider';

export const DialogRoot = D.Root;
export const DialogTrigger = D.Trigger;
export const DialogClose = D.Close;
export const DialogPortal = D.Portal;

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

const DIALOG_SIZE: Record<DialogSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-xl',
  xl: 'max-w-3xl',
};

export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof D.Overlay>,
  React.ComponentPropsWithoutRef<typeof D.Overlay>
>(function DialogOverlay({ className, ...props }, ref) {
  return (
    <D.Overlay
      ref={ref}
      className={cn(
        'fixed inset-0 z-(--z-dialog) bg-overlay backdrop-blur-[2px]',
        'data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out',
        className,
      )}
      {...props}
    />
  );
});

export interface DialogContentProps
  extends Omit<React.ComponentPropsWithoutRef<typeof D.Content>, 'title'> {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Renders the title for screen readers only (rare — prefer a visible title). */
  hideTitle?: boolean;
  size?: DialogSize;
  /** Sticky action row at the bottom; buttons are laid out in logical order. */
  footer?: React.ReactNode;
  /** Hide the ✕ button when the dialog must be resolved by its actions. */
  hideClose?: boolean;
  /** Accessible name of the ✕. Defaults to the localised `close` label from `<UIProvider>` (UX-42). */
  closeLabel?: string;
  /**
   * Removes the body padding, for content that fills the dialog with chrome of its own. With
   * `hideTitle` and `hideClose` and no description, the header row goes too.
   */
  bare?: boolean;
}

/**
 * Centred modal for a decision or a short form (export options, delete
 * confirmation, sign-in). Anything long, or anything the user needs to touch
 * while looking at the canvas, belongs in a `<Sheet>` instead.
 */
export const DialogContent = React.forwardRef<React.ElementRef<typeof D.Content>, DialogContentProps>(
  function DialogContent(
    { title, description, hideTitle, size = 'md', footer, hideClose, closeLabel, bare, className, children, ...props },
    ref,
  ) {
    const labels = useUILabels();
    const header = !hideTitle || Boolean(description) || !hideClose;
    return (
      <D.Portal>
        <DialogOverlay />
        <D.Content
          ref={ref}
          className={cn(
            'fixed start-1/2 top-1/2 z-(--z-dialog) w-[calc(100vw-2rem)] -translate-y-1/2',
            'flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden',
            'rounded-xl border border-line bg-surface shadow-xl',
            'data-[state=open]:animate-scale-in data-[state=closed]:animate-scale-out',
            'ltr:-translate-x-1/2 rtl:translate-x-1/2',
            DIALOG_SIZE[size],
            className,
          )}
          {...props}
        >
          {header ? (
          <div className="flex items-start gap-3 px-5 pt-5 pb-3">
            <div className="min-w-0 flex-1">
              {hideTitle ? (
                <D.Title className="sr-only">{title}</D.Title>
              ) : (
                <D.Title className="font-display text-lg leading-snug font-semibold text-ink">{title}</D.Title>
              )}
              {description ? (
                <D.Description className="mt-1 text-sm text-ink-2">{description}</D.Description>
              ) : null}
            </div>
            {!hideClose ? (
              <D.Close asChild>
                <IconButton label={closeLabel ?? labels.close} size="sm" variant="ghost" className="-me-2 -mt-1">
                  <X />
                </IconButton>
              </D.Close>
            ) : null}
          </div>
          ) : (
            <D.Title className="sr-only">{title}</D.Title>
          )}

          <div className={cn('min-h-0 flex-1 overflow-y-auto', !bare && 'px-5 pb-5')}>{children}</div>

          {footer ? (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-paper-2 px-5 py-3">
              {footer}
            </div>
          ) : null}
        </D.Content>
      </D.Portal>
    );
  },
);

export interface DialogProps extends DialogContentProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
  modal?: boolean;
}

/** One-element convenience wrapper: `<Dialog title="…" trigger={<Button/>}>…</Dialog>`. */
export function Dialog({ open, defaultOpen, onOpenChange, trigger, modal, ...content }: DialogProps) {
  return (
    <D.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} modal={modal}>
      {trigger ? <D.Trigger asChild>{trigger}</D.Trigger> : null}
      <DialogContent {...content} />
    </D.Root>
  );
}
